import { useState } from 'react';

import { JustificationDetail } from './JustificationDetail';
import { JustificationList } from './JustificationList';
import styles from './JustificationScreen.module.css';

/**
 * The justifications screen (M-11, Task 4) — `/justifications`, a nav
 * entry that already existed in `navigation.ts` but pointed nowhere: no
 * screen was ever wired to it, and the legacy's own supervisor list page
 * rejected the only role its own sidebar link was shown to (spec §0).
 * Reachable for a session holding any of `justification.view`/`create`/
 * `answer`/`decide` (`src/lib/permissions/justifications.ts`'s own
 * `canReadJustifications`, mirroring `JustificationPolicy::mayRead()`).
 *
 * ## The list is never unmounted to show a detail
 *
 * The identical shape `EscalationScreen.tsx`'s own docblock explains at
 * length: an early return that swaps `<JustificationList>` for
 * `<JustificationDetail>` destroys the list's own `draftFilters`/
 * `appliedFilters`/`cursor` state the moment a row opens, so `onBack` would
 * come back to a freshly-mounted, unfiltered list. The list stays mounted
 * in a `hidden` div; opening a row only adds `<JustificationDetail>` as a
 * sibling.
 *
 * ## The `hidden` div carries NO `className` (M-10's C-2, restated here)
 *
 * An author-origin `display` declaration beats the UA stylesheet's
 * `[hidden] { display: none }` — cascade ORIGIN wins before specificity is
 * even considered. `EscalationScreen.tsx` shipped exactly that defect once
 * (`className={styles['listPage']}` on the same element as `hidden`) and
 * `CaseScreen.tsx` is the pattern that never had it: a BARE `hidden` div,
 * with the list's own layout living on a nested div inside it instead.
 * Copied here for the identical reason.
 *
 * ## There is no justifications panel on the case screen (spec §3)
 *
 * This module touches no case, no patient, no device, no dispensary — spec
 * J-8 says so explicitly, and unlike escalations there is no case-detail
 * integration to wire up here.
 */

export interface JustificationScreenProps {
  permissions: readonly string[];
  currentUserUuid: string;
}

export function JustificationScreen({ permissions, currentUserUuid }: JustificationScreenProps) {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className={styles['page']}>
      {selected === null ? null : (
        <JustificationDetail
          uuid={selected}
          onBack={() => {
            setSelected(null);
          }}
        />
      )}

      {/* `hidden`, never a ternary that unmounts — see this file's own
          docblock. Bare — no `className` here; the list's own layout lives
          inside `JustificationList` itself, not on this element (C-2). */}
      <div hidden={selected !== null}>
        <JustificationList
          permissions={permissions}
          active={selected === null}
          currentUserUuid={currentUserUuid}
          onOpen={(uuid) => {
            setSelected(uuid);
          }}
        />
      </div>
    </div>
  );
}
